import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import PageHero from './PageHero';
import SectionHeader from './SectionHeader';
import { blogPosts, BlogCard } from './BlogPosts';
import { motion } from '../motion';
import '../styles/pages/blog.css';

gsap.registerPlugin(ScrollTrigger);

/* ---------------------------------------------------------------------------
   Editorial article shell used by every /blog/* post.

   <BlogArticle slug="…" tags={['Public Sector','Compliance']}
     title={<>… <em>June 2026</em></>} lead="…" figure={<ADMDirectiveHero />}
     cta={{ title, text, label }}>
     <ArticleH2 id="deadline">The deadline</ArticleH2>
     <p>…</p>
   </BlogArticle>

   - Dark PageHero (crumbs, tags, date, reading time), figure overlapping it.
   - Sticky rail on desktop: reading progress + "On this page" (built from
     h2[id] inside the article) with the current section highlighted.
   - Collapsible contents on mobile.
   - Light note-yellow CTA card closing the article (the global footer
     already opens with the dark CTA band), then a sand "keep reading" rail.
   All scroll logic lives in one gsap.context and is reverted on unmount.
   --------------------------------------------------------------------------- */

export function ArticleH2({ id, children }) {
  return <h2 id={id} tabIndex={-1} data-split>{children}</h2>;
}

export function ArticleH3({ children }) {
  return <h3 data-reveal>{children}</h3>;
}

/** Pull quote. Wraps text that already exists in the article (moved, not duplicated). */
export function PullQuote({ children }) {
  return (
    <blockquote className="blog-pull" data-reveal>
      <span className="blog-pull__mark" aria-hidden="true">&ldquo;</span>
      <p>{children}</p>
    </blockquote>
  );
}

/** Week-by-week plan: items = [['Week 1', 'text'], …] */
export function WeekPlan({ items, label }) {
  return (
    <ol className="blog-weeks" aria-label={label} data-reveal-stagger>
      {items.map(([when, text]) => (
        <li key={when}>
          <span className="blog-weeks__when">{when}</span>
          <span className="blog-weeks__text">{text}</span>
        </li>
      ))}
    </ol>
  );
}

function tocFrom(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll('h2[id]')).map((h) => ({
    id: h.id,
    // Skip screen-reader-only numbering (e.g. "1) ") since the list shows its own index.
    label: Array.from(h.childNodes)
      .filter((n) => !(n.classList && n.classList.contains('visually-hidden')))
      .map((n) => n.textContent)
      .join('')
      .replace(/\s+/g, ' ')
      .trim(),
  }));
}

export default function BlogArticle({ slug, tags = [], title, lead, figure, figureClass = '', proseClass = '', cta, children }) {
  const post = blogPosts.find((p) => p.slug === slug) || {};
  const related = blogPosts.filter((p) => p.slug !== slug);
  const articleRef = useRef(null);
  const railRef = useRef(null);
  const pctRef = useRef(null);
  const [toc, setToc] = useState([]);

  // Build the contents list from the rendered headings.
  useLayoutEffect(() => { setToc(tocFrom(articleRef.current)); }, [slug]);

  // Reading progress + active section.
  useLayoutEffect(() => {
    const article = articleRef.current;
    const rail = railRef.current;
    if (!article || !rail || !toc.length) return undefined;
    const setActive = (id) => {
      rail.querySelectorAll('[data-toc-id]').forEach((a) => {
        const on = a.getAttribute('data-toc-id') === id;
        a.classList.toggle('is-active', on);
        if (on) a.setAttribute('aria-current', 'location'); else a.removeAttribute('aria-current');
      });
    };
    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: article,
        start: 'top 30%',
        end: 'bottom 85%',
        onUpdate: (self) => {
          const p = self.progress;
          rail.style.setProperty('--read', p.toFixed(4));
          if (pctRef.current) pctRef.current.textContent = `${Math.round(p * 100)}%`;
        },
      });
      toc.forEach(({ id }, i) => {
        const el = document.getElementById(id);
        if (!el) return;
        ScrollTrigger.create({
          trigger: el,
          start: 'top 45%',
          onEnter: () => setActive(id),
          onLeaveBack: () => setActive(i > 0 ? toc[i - 1].id : null),
        });
      });
    }, article);
    return () => ctx.revert();
  }, [toc]);

  const jump = (e, id) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    motion.scrollTo(el);
    try { el.focus({ preventScroll: true }); } catch (err) { /* noop */ }
  };

  const tocList = (
    <ol className="blog-toc__list">
      {toc.map(({ id, label }, i) => (
        <li key={id}>
          <a href={`#${id}`} data-toc-id={id} onClick={(e) => jump(e, id)}>
            <span className="blog-toc__num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
            <span>{label}</span>
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <>
      <PageHero
        variant="dark"
        className="blog-hero"
        crumbs={[{ to: '/', label: 'Home' }, { to: '/blog', label: 'Blog' }, { label: post.category || 'Article' }]}
        eyebrow={tags.join(' · ')}
        title={title}
        lead={lead}
      >
        <ul className="blog-hero__meta">
          <li><i className="fa-regular fa-calendar" aria-hidden="true" /><time dateTime={post.iso}>{post.date}</time></li>
          {post.read ? <li><i className="fa-regular fa-clock" aria-hidden="true" />{post.read} min read</li> : null}
          <li><i className="fa-solid fa-leaf" aria-hidden="true" />Moneli Insights</li>
        </ul>
      </PageHero>

      <section className="section section--light blog-article">
        <div className="container">
          {figure && (
            <figure className={`blog-figure ${figureClass}`} data-reveal="scale">
              {figure}
            </figure>
          )}

          <div className="blog-layout">
            <aside className="blog-rail" ref={railRef} aria-label="Article navigation">
              <div className="blog-rail__inner">
                <Link to="/blog" className="blog-rail__back">
                  <i className="fa-solid fa-arrow-left" aria-hidden="true" /> Back to Blog
                </Link>
                <div className="blog-progress" aria-hidden="true">
                  <span className="blog-progress__label">Reading</span>
                  <span className="blog-progress__pct" ref={pctRef}>0%</span>
                  <span className="blog-progress__track"><span className="blog-progress__fill" /></span>
                </div>
                {toc.length > 0 && (
                  <nav className="blog-toc" aria-label="On this page">
                    <p className="blog-toc__title">On this page</p>
                    {tocList}
                  </nav>
                )}
              </div>
            </aside>

            <div className="blog-main">
              {toc.length > 0 && (
                <details className="blog-toc-m">
                  <summary>
                    <span><i className="fa-solid fa-list-ul" aria-hidden="true" /> On this page</span>
                    <i className="fa-solid fa-chevron-down blog-toc-m__chev" aria-hidden="true" />
                  </summary>
                  <nav className="blog-toc" aria-label="On this page (mobile)">{tocList}</nav>
                </details>
              )}

              <article className={`blog-prose ${proseClass}`} ref={articleRef}>
                {children}
              </article>

              {cta && (
                <aside className="blog-cta" data-reveal aria-labelledby={`${slug}-cta`}>
                  <span className="blog-cta__pin" aria-hidden="true" />
                  <p className="blog-cta__kicker">Work with Moneli</p>
                  <h3 className="blog-cta__title" id={`${slug}-cta`}>{cta.title}</h3>
                  <p className="blog-cta__text">{cta.text}</p>
                  <Link to="/contact" className="btn btn-ink" data-magnetic>
                    {cta.label} <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </Link>
                </aside>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section section--sand blog-more" aria-labelledby="blog-more-title">
        <div className="container">
          <SectionHeader
            eyebrow="Keep reading"
            title={<>More from the <em>blog</em></>}
            lead="Practical guides on AI automation, procurement, privacy, and operations."
            id="blog-more-title"
          />
          <div className="grid-3 blog-grid" data-reveal-stagger>
            {related.map((p) => <BlogCard key={p.slug} post={p} />)}
          </div>
          <p className="blog-more__all">
            <Link to="/blog" className="link-arrow">All articles <i className="fa-solid fa-arrow-right" aria-hidden="true" /></Link>
          </p>
        </div>
      </section>
    </>
  );
}
